import { useCallback, useEffect, useMemo } from 'react';
import { Cuboid, CuboidEditor, TransformSpace } from '@abyss/3d-viewer';
import { CuboidClickMode, LabellingCuboid } from '@/types';
import { useVisibleCuboids } from '@/components/AssetLabelling/hooks';
import { useCuboidState } from '../hooks/useCuboidState';
import { useCuboidDataHandlers } from '../hooks/useCuboidDataHandlers';
import { useSaveCuboid } from '@/modules/cuboids/hooks/useSaveCuboid';
import { useSelectCuboid } from '@/modules/cuboids/hooks/useSelectCuboid';
import { useCuboidStyleHandlers } from '../hooks/useCuboidStyleHandlers';
import { useMakeStyledCuboid } from '@/modules/cuboids/hooks/useMakeStyledCuboid';
import { useAtom, useAtomValue } from 'jotai';

export const SceneCuboids = () => {
  const cuboidState = useCuboidState();
  const [currentCuboid, setCurrentCuboid] = useAtom(cuboidState.currentCuboid);
  const cuboidTransformMode = useAtomValue(cuboidState.cuboidTransformMode);
  const cuboidClickMode = useAtomValue(cuboidState.cuboidClickMode);
  const [isCuboidEditorEnabled, setIsCuboidEditorEnabled] = useAtom(
    cuboidState.isCuboidEditorEnabled
  );

  const dataHandlers = useCuboidDataHandlers();
  const styleHandlers = useCuboidStyleHandlers();

  const saveCuboid = useSaveCuboid(dataHandlers);
  const selectCuboid = useSelectCuboid(cuboidState, styleHandlers);
  const makeStyledCuboid = useMakeStyledCuboid(styleHandlers);

  const cuboids = useVisibleCuboids();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Esc key
      const { altKey, ctrlKey, metaKey, shiftKey } = event;
      if (
        currentCuboid?.id &&
        event.key === 'Escape' &&
        !altKey &&
        !ctrlKey &&
        !metaKey &&
        !shiftKey
      ) {
        // saving current cuboid here
        setIsCuboidEditorEnabled(false);

        event.stopPropagation();
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentCuboid?.id, setIsCuboidEditorEnabled]);

  const onCuboidClick = useCallback(
    (cuboid: LabellingCuboid) => {
      if (cuboidClickMode === CuboidClickMode.AddCuboid) return;
      selectCuboid(cuboid);
    },
    [cuboidClickMode, selectCuboid]
  );

  const cuboidsComp = useMemo(
    () =>
      cuboids?.map((cuboid) => {
        if (cuboid.id === currentCuboid?.id && isCuboidEditorEnabled) return undefined;
        const styledCuboid = makeStyledCuboid(cuboid);
        return <Cuboid {...styledCuboid} key={cuboid.id} onClick={() => onCuboidClick(cuboid)} />;
      }),
    [cuboids, currentCuboid?.id, onCuboidClick, makeStyledCuboid, isCuboidEditorEnabled]
  );

  const currentCuboidStyle = useMemo(() => {
    if (!currentCuboid) return undefined;
    return styleHandlers.makeCurrentCuboidStyle(currentCuboid);
  }, [currentCuboid, styleHandlers]);

  return (
    <>
      {cuboidsComp}
      {currentCuboid && (
        <CuboidEditor
          isEnabled={isCuboidEditorEnabled}
          cuboid={currentCuboid}
          setCuboid={setCurrentCuboid}
          cuboidStyle={currentCuboidStyle}
          transformMode={cuboidTransformMode}
          transformSpace={TransformSpace.Local}
          onSaveCuboid={saveCuboid}
        />
      )}
    </>
  );
};
